// src/features/tickets/services/TicketProofreadService.ts
//
// Schlanker Wrapper um den Netlify-Endpoint /.netlify/functions/proofread-text.
//
// Der Aufruf läuft über die zentrale Aufruffunktion netlifyFetch, die das
// Sitzungs-Token der angemeldeten Person mitgibt (Phase 0, Punkt 1). Der
// Endpoint verlangt diesen Ausweis; ohne ihn antwortet er mit 401.
//
// Anders als zuvor (Klartext rein/raus) arbeitet die Pruefung jetzt auf einer
// STRUKTURIERTEN Repraesentation des Editor-Inhalts. Damit bleibt jede
// Formatierung verlustfrei erhalten: Inline- und Block-Formate werden als opake
// IDs ("styles"/"blocks") an die KI gegeben, die sie nur durchreicht — die KI
// kann Formatdefinitionen weder veraendern noch erfinden, weil die Definitionen
// ausschliesslich auf unserer Seite liegen. Bilder werden gar nicht erst an die
// KI geschickt (sie sind im Snapshot als Embeds gehalten), sondern nur als
// Marker {"img": id} durchgereicht.
//
// Zwei Modi:
//   - 'full': vollstaendige Pruefung fuer Kundenmails (Korrektur, Schriftbild,
//     Ton, Direktkorrekturen + Hinweise). Bekommt zusaetzlich "context".
//   - 'note': stark reduzierte Pruefung fuer interne Notizen (nur Rechtschreibung,
//     Grammatik, Zeichensetzung). Kein "context", keine Hinweise.
//
// Persistiert nichts und versendet nichts.

import { netlifyFetch } from '../../../shared/services/netlifyFetch';

export type ProofreadMode = 'full' | 'note';

/**
 * Beliebige Quill-Attribut-Map (z. B. { bold: true }, { color: '#C00000' },
 * { list: 'bullet' }). Wird auf unserer Seite VERBATIM gehalten und nie
 * interpretiert — das ist die Grundlage der Verlustfreiheit fuer beliebige
 * (auch kuenftige) Formate.
 */
export type ProofreadAttributes = Record<string, unknown>;

/** Ein formatierter Textabschnitt: Text + Stil-ID (Verweis in die styles-Map). */
export interface ProofreadTextRun {
  t: string;
  s: string;
}

/** Ein Bild-Abschnitt: nur die Marker-ID (Bilddaten bleiben lokal im Snapshot). */
export interface ProofreadImageRun {
  img: string;
}

export type ProofreadRun = ProofreadTextRun | ProofreadImageRun;

/** Eine Zeile: Block-ID (Verweis in die blocks-Map) + Abschnittsliste. */
export interface ProofreadLine {
  b: string;
  runs: ProofreadRun[];
}

/** Stil-ID -> Inline-Attribute (opak fuer die KI). */
export type ProofreadStyleMap = Record<string, ProofreadAttributes>;

/** Block-ID -> Zeilen-/Block-Attribute (opak fuer die KI). */
export type ProofreadBlockMap = Record<string, ProofreadAttributes>;

/** Eine Nachricht im bisherigen Verlauf. "customer" = Anfragender. */
export interface ProofreadThreadMessage {
  from: 'customer' | 'staff';
  text: string;
}

/**
 * Kontext fuer die Voll-Pruefung. Schluessel sind bewusst snake_case, weil das
 * Objekt 1:1 als JSON an das Modell geht und der Prompt diese Namen referenziert.
 */
export interface ProofreadContext {
  /** Korrekt geschriebener Name des Anfragenden (oder null). */
  requester_name: string | null;
  /** Fertig formulierte Anrede für den Anfang der Nachricht (oder null), z. B. "Guten Tag Herr Müller,". */
  salutation: string | null;
  /** Name des bearbeitenden Mitarbeiters fuer die Grussformel (oder null). */
  sender_name: string | null;
  /**
   * Ist im Composer gerade ein terminierter Versand eingestellt? Der Server
   * leitet daraus die Grussformel ab: bei terminiertem Versand nie die
   * Freitags-Wochenendform, weil die Mail dann nicht am Freitag rausgeht.
   * Die Formel selbst bildet der Server, nicht der Client.
   */
  is_scheduled_send: boolean;
  /** Dateinamen der aktuell angehaengten Anhaenge (kann leer sein). */
  attachments: string[];
  /** Vollstaendiger vorausgegangener Verlauf (Server kuerzt bei Budgetueberschreitung). */
  thread: ProofreadThreadMessage[];
}

export type ProofreadHintType =
  | 'unanswered_question'
  | 'missing_attachment'
  | 'name_corrected'
  | 'missing_closing'
  | 'conflict';

/** Ein Hinweis an den Bearbeiter (wird angezeigt, nie in die Nachricht uebernommen). */
export interface ProofreadHint {
  type: ProofreadHintType;
  message?: string;
}

/** Anfrage an den Endpoint. context nur fuer mode='full' relevant. */
export interface ProofreadRequest {
  mode: ProofreadMode;
  styles: ProofreadStyleMap;
  blocks: ProofreadBlockMap;
  lines: ProofreadLine[];
  context?: ProofreadContext | null;
}

export interface ProofreadResult {
  success: boolean;
  lines?: ProofreadLine[];
  hints?: ProofreadHint[];
  error?: string;
}

/**
 * Ein eingebettetes Objekt (Bild) aus dem Quill-Delta. Wird ausschliesslich
 * lokal im Snapshot gehalten und NIE an die KI geschickt; beim Uebernehmen
 * verbatim wieder eingesetzt.
 */
export interface ProofreadEmbed {
  insert: Record<string, unknown>;
  attributes?: ProofreadAttributes;
}

/**
 * Selbst-enthaltener Schnappschuss des Entwurfs zum Pruefzeitpunkt.
 *   - styles/blocks/lines gehen an den Server.
 *   - embedById bleibt lokal und dient dem verbatim Wiedereinsetzen der Bilder.
 * ProofreadButton haelt den Snapshot zwischen Pruefung und Uebernahme; das
 * spaetere Uebernehmen validiert die KI-Antwort gegen genau diesen Snapshot,
 * sodass nur bekannte IDs akzeptiert werden.
 */
export interface ProofreadDraftSnapshot {
  styles: ProofreadStyleMap;
  blocks: ProofreadBlockMap;
  lines: ProofreadLine[];
  embedById: Record<string, ProofreadEmbed>;
}

class TicketProofreadService {
  private readonly endpoint = '/.netlify/functions/proofread-text';

  /**
   * Schickt die strukturierte Repraesentation des Entwurfs zur KI-Pruefung und
   * gibt die (noch unvalidierte) strukturierte Antwort zurueck. Die strikte
   * Validierung gegen den Snapshot passiert im Aufrufer (ProofreadButton),
   * weil nur dort die bekannten IDs vorliegen.
   */
  async proofread(request: ProofreadRequest): Promise<ProofreadResult> {
    try {
      const response = await netlifyFetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: request.mode,
          styles: request.styles,
          blocks: request.blocks,
          lines: request.lines,
          context: request.context ?? null
        })
      });

      if (!response.ok) {
        let serverMessage = '';
        try {
          const errData = await response.json();
          serverMessage = errData && typeof errData.error === 'string' ? errData.error : '';
        } catch {
          serverMessage = '';
        }
        console.error('[TicketProofreadService.proofread] HTTP-Error:', response.status);
        return {
          success: false,
          error: serverMessage || `Prüfung fehlgeschlagen (HTTP ${response.status}).`
        };
      }

      const data = await response.json();
      if (!data || !Array.isArray(data.lines)) {
        return { success: false, error: 'Unerwartete Antwort vom Server.' };
      }

      const hints: ProofreadHint[] = Array.isArray(data.hints) ? data.hints : [];
      return { success: true, lines: data.lines as ProofreadLine[], hints };
    } catch (error) {
      console.error('[TicketProofreadService.proofread] Unexpected error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler bei der Prüfung'
      };
    }
  }
}

// Singleton-Export
export const ticketProofreadService = new TicketProofreadService();
